"use client";

import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import AuthService from "../../services/auth-service";
import { Button } from "./button";
import { SignIn } from "./login-2";
import { Signup } from "./signup";

interface OAuthPageProps {
  onAuthComplete?: (token: string) => void;
}

export const OAuthPage: React.FC<OAuthPageProps> = ({ onAuthComplete }) => {
  const [authMode, setAuthMode] = useState<"login" | "signup" | "processing" | "success" | "error">("login");
  const [token, setToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const searchParams = useSearchParams()[0];
  const navigate = useNavigate();

  // Check if this is a desktop app OAuth request
  const desktopRedirect = searchParams.get("desktop_redirect");
  const isDesktopAppRequest = !!desktopRedirect;
  const appName = searchParams.get("app_name") || "Caude Desktop App";

  // Get API base URL from environment variables
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://kaya-servers.onrender.com/api';

  // Handle successful authentication
  const handleAuthSuccess = (authToken: string) => {
    setAuthMode("success");
    setToken(authToken);
    
    // If this is for a desktop app, prepare to redirect
    if (isDesktopAppRequest) {
      // In a real implementation, you might want to show a button to manually trigger the redirect
      // or automatically redirect after a short delay
      setTimeout(() => {
        redirectToDesktopApp(authToken);
      }, 2000);
    } else {
      // For web OAuth, redirect to profile page
      setTimeout(() => {
        navigate("/profile");
      }, 2000);
    }
  };

  // Handle authentication failure
  const handleAuthFailure = (errorMessage: string) => {
    setAuthMode("error");
    setError(errorMessage);
  };

  // Redirect to desktop app with token
  const redirectToDesktopApp = (authToken: string) => {
    try {
      // Create the redirect URL with the token
      const redirectUrl = `${desktopRedirect}?token=${authToken}`;
      
      // Attempt to redirect
      window.location.href = redirectUrl;
    } catch (err) {
      setError("Failed to redirect to desktop app. Please copy the token manually.");
    }
  };

  // Handle Google OAuth
  const handleGoogleOAuth = async () => {
    try {
      setAuthMode("processing");
      
      // If this is a desktop app request, we need to modify the redirect URI
      if (isDesktopAppRequest && desktopRedirect) {
        // For desktop app, we'll use the provided redirect URI
        // We need to pass the desktop redirect info in the state parameter
        const stateObj = {
          desktop_redirect: desktopRedirect
        };
        const state = encodeURIComponent(JSON.stringify(stateObj));
        
        // Get the redirect URI based on environment
        const redirectUri = `${API_BASE_URL.replace('/api', '')}/api/auth/callback/google`;
        const encodedRedirectUri = encodeURIComponent(redirectUri);
        
        // Construct the Google OAuth URL with state parameter
        const googleOAuthUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${process.env.REACT_APP_GOOGLE_CLIENT_ID}&redirect_uri=${encodedRedirectUri}&response_type=code&scope=email profile&access_type=offline&state=${state}`;
        window.location.href = googleOAuthUrl;
      } else {
        // For web, use the standard flow
        await AuthService.loginWithGoogle();
      }
    } catch (err: any) {
      handleAuthFailure(err.message || "Failed to initiate Google OAuth");
    }
  };

  // Handle GitHub OAuth
  const handleGitHubOAuth = async () => {
    try {
      setAuthMode("processing");
      
      // If this is a desktop app request, we need to modify the redirect URI
      if (isDesktopAppRequest && desktopRedirect) {
        // For desktop app, we'll use the provided redirect URI
        // We need to pass the desktop redirect info in the state parameter
        const stateObj = {
          desktop_redirect: desktopRedirect
        };
        const state = encodeURIComponent(JSON.stringify(stateObj));
        
        // Get the redirect URI based on environment
        const redirectUri = `${API_BASE_URL.replace('/api', '')}/api/auth/callback/github`;
        const encodedRedirectUri = encodeURIComponent(redirectUri);
        
        // Construct the GitHub OAuth URL with state parameter
        const githubOAuthUrl = `https://github.com/login/oauth/authorize?client_id=${process.env.REACT_APP_GITHUB_CLIENT_ID}&redirect_uri=${encodedRedirectUri}&scope=user:email&state=${state}`;
        window.location.href = githubOAuthUrl;
      } else {
        // For web, use the standard flow
        await AuthService.loginWithGitHub();
      }
    } catch (err: any) {
      handleAuthFailure(err.message || "Failed to initiate GitHub OAuth");
    }
  };

  // Render processing state
  if (authMode === "processing") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="w-full max-w-md bg-card rounded-2xl shadow-xl p-8 border border-border">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <h2 className="mt-6 text-2xl font-bold text-foreground">Processing Authentication</h2>
            <p className="mt-4 text-muted-foreground">
              Redirecting to authentication provider...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Render success state
  if (authMode === "success") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="w-full max-w-md bg-card rounded-2xl shadow-xl p-8 border border-border">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100">
              <svg className="h-10 w-10 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="mt-6 text-2xl font-bold text-foreground">Authentication Successful!</h2>
            <p className="mt-4 text-muted-foreground">
              {isDesktopAppRequest 
                ? `Redirecting you back to ${appName}...` 
                : "Redirecting you to your profile..."}
            </p>
            
            {isDesktopAppRequest && !token && (
              <div className="mt-6 p-4 bg-yellow-50 rounded-lg">
                <p className="text-sm text-yellow-800">
                  If you are not automatically redirected, please click the button below:
                </p>
                <Button 
                  className="mt-4 w-full" 
                  onClick={() => token && redirectToDesktopApp(token)}
                >
                  Open {appName}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Render error state
  if (authMode === "error") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="w-full max-w-md bg-card rounded-2xl shadow-xl p-8 border border-border">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100">
              <svg className="h-10 w-10 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="mt-6 text-2xl font-bold text-foreground">Authentication Failed</h2>
            <p className="mt-4 text-muted-foreground">{error}</p>
            <Button 
              className="mt-6 w-full" 
              onClick={() => setAuthMode("login")}
            >
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Render login or signup form with OAuth options
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-md">
        {isDesktopAppRequest && (
          <div className="mb-6 text-center">
            <h1 className="text-2xl font-bold text-foreground">Connect to {appName}</h1>
            <p className="mt-2 text-muted-foreground">
              Sign in or create an account to connect with {appName}
            </p>
          </div>
        )}
        
        {authMode === "login" ? (
          <div>
            <SignIn 
              onSwitchToSignup={() => setAuthMode("signup")}
              onClose={() => {
                // Handle close if needed
              }}
            />
            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-background text-muted-foreground">
                    Or continue with
                  </span>
                </div>
              </div>

              <div className="mt-6 grid grid-cols-2 gap-3">
                <Button
                  variant="outline"
                  onClick={handleGoogleOAuth}
                  className="w-full"
                >
                  <svg className="h-5 w-5 text-red-500" viewBox="0 0 24 24">
                    <path
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      fill="currentColor"
                    />
                    <path
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      fill="currentColor"
                    />
                    <path
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      fill="currentColor"
                    />
                    <path
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      fill="currentColor"
                    />
                  </svg>
                  <span className="ml-2">Google</span>
                </Button>

                <Button
                  variant="outline"
                  onClick={handleGitHubOAuth}
                  className="w-full"
                >
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path
                      d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"
                    />
                  </svg>
                  <span className="ml-2">GitHub</span>
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div>
            <Signup 
              onSwitchToLogin={() => setAuthMode("login")}
              onClose={() => {
                // Handle close if needed
              }}
            />
            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-background text-muted-foreground">
                    Or sign up with
                  </span>
                </div>
              </div>

              <div className="mt-6 grid grid-cols-2 gap-3">
                <Button
                  variant="outline"
                  onClick={handleGoogleOAuth}
                  className="w-full"
                >
                  <svg className="h-5 w-5 text-red-500" viewBox="0 0 24 24">
                    <path
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      fill="currentColor"
                    />
                    <path
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      fill="currentColor"
                    />
                    <path
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      fill="currentColor"
                    />
                    <path
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      fill="currentColor"
                    />
                  </svg>
                  <span className="ml-2">Google</span>
                </Button>

                <Button
                  variant="outline"
                  onClick={handleGitHubOAuth}
                  className="w-full"
                >
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path
                      d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"
                    />
                  </svg>
                  <span className="ml-2">GitHub</span>
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OAuthPage;